import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Container, Form } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import getRecaptchaToken from '../helpers/getRecaptchaToken';
import isStrongPassword from 'validator/lib/isStrongPassword';

import '../styles/AccountRecoveryConfirmationPage.css';
import AppContext from './context/AppContext';
import { bool } from 'prop-types';

const AccountRecoveryConfirmationPage = ({ isMobile }) => {
    const { appContext } = useContext(AppContext);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isValidRequest, setIsValidRequest] = useState(true);
    const [isConfirming, setIsConfirming] = useState(true);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [startRecovery, setStartRecovery] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const email = searchParams.get('email');
    const code = searchParams.get('code');
    const handleVerificationRequest = useCallback(async () => {
        try {
            if (!email
                || !isEmail(email)
                || !code
                || !code.length) {
                setIsValidRequest(false);
                setIsConfirming(false);
                return;
            }
            setIsConfirming(true);
            setIsConfirmed(false);
            setErrorMessage(null);
            const response = await fetch(new URL('/api/user/recover/confirm', process.env.APP_URL), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({
                    token: await getRecaptchaToken(appContext.config.recaptchaSiteKey),
                    email,
                    code,
                    newPassword,
                }),
            });
            const data = await response.json();
            setIsConfirming(false);
            if (!data.success) {
                if (data.error) {
                    setErrorMessage(data.error);
                }
                setIsConfirmed(false);
                return;
            }
            setIsConfirmed(true);
        } catch (e) {
            setIsConfirming(false);
            setErrorMessage(e.message);
        }
    }, [email, code, newPassword]);
    useEffect(() => {
        if (startRecovery === true) {
            handleVerificationRequest();
        }
    }, [startRecovery, handleVerificationRequest]);
    useEffect(() => {
        const navigateTimeoutFunc = () => {
            navigate('/');
        };
        const navigateTimer = null;
        const NAVIGATE_TIMEOUT_MS = 3000;
        if (isConfirmed === true) {
            setTimeout(navigateTimeoutFunc, NAVIGATE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(navigateTimer);
        };
    }, [isConfirmed]);
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!newPassword || !isStrongPassword(newPassword)) 
            return;
        setStartRecovery(true);
    };
    return (<Container
        fluid>
        <div
            className={'page--section mt-4'}>
            <h2>Recover Account</h2>
            {startRecovery === false && (<>
                <Form
                    onSubmit={handleSubmit}>
                    <Form.Group
                        className={'mb-2'}
                        controlId={'accountRecoveryForm.Password'}>
                        <Form.Label>New password</Form.Label>
                        <Form.Control
                            style={{ maxWidth: '300px' }}
                            type={'password'}
                            name={'new-password'}
                            placeholder={'Enter a strong password...'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            isValid={isStrongPassword(newPassword)}
                            isInvalid={newPassword !== '' && !isStrongPassword(newPassword)} />
                    </Form.Group>
                    <Form.Group
                        controlId={'accountRecoveryForm.Submit'}>
                        <Button
                            type={'submit'}
                            onClick={handleSubmit}
                            disabled={!newPassword || !isStrongPassword(newPassword)}>Recover</Button>
                    </Form.Group>
                </Form>
            </>)}
            {startRecovery === true && (<>
                {!isValidRequest && (<p>Invalid recovery request.</p>)}
                {isConfirming === true && (<p>Confirming request...</p>)}
                {(isConfirming === false && isConfirmed === false) && (<p>Request failed.</p>)}
                {isConfirmed === true && (
                    <p
                        style={{ color: 'green' }}>Your password has been reset successfully. Redirecting...</p>
                )}
                {(!isConfirming && errorMessage !== null) && (
                    <p
                        style={{ color: 'red' }}>Error: {errorMessage ?? 'Failed.'}</p>
                )}
            </>)}
        </div>
    </Container>);
};

AccountRecoveryConfirmationPage.propTypes = {
    isMobile: bool,
};

export default AccountRecoveryConfirmationPage;